import type { Order } from "@openbook-dex/openbook/lib/market";
import { useConnection } from "@solana/wallet-adapter-react";
import { Transaction } from "@solana/web3.js";
import { chunks } from "@stepfinance/common";
import type { OpenbookOpenOrder, OpenOrdersResponse } from "@stepfinance/dex";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useLanguageContext } from "../../../contexts/language/language";
import { useConnectedWallet } from "../../../hooks";
import { useOpenbookOrdersQuery } from "../../../hooks/queries/dex/useOpenbookOrdersQuery";
import { notify } from "../../../utils/notifications";
import { handlePreSendTxnError, sendAllTransactions, sendTransaction } from "../../../utils/transactions";

const BATCH_SIZE = 3;

export const useCancelOpenbookOrders = () => {
  const queryClient = useQueryClient();
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { data } = useOpenbookOrdersQuery();
  const {
    currentLanguageData: {
      common,
      pages: {
        swapLP: { notifications: swapNotifications },
        dashboard: {
          modules: { DEXModule },
        },
      },
    },
  } = useLanguageContext();

  return useMutation({
    mutationFn: async (orders: OpenbookOpenOrder[]) => {
      if (!wallet) {
        return;
      }
      const txs = chunks(orders, BATCH_SIZE).map((chunk) => {
        const transaction = new Transaction();
        chunk.forEach((order) => {
          const market = data?.marketsMap.get(order.marketAddress.toBase58());
          if (!market) {
            return;
          }
          transaction.add(market.makeMatchOrdersTransaction(5));
          transaction.add(market.makeCancelOrderInstruction(connection, wallet.publicKey, order as unknown as Order));
          transaction.add(market.makeMatchOrdersTransaction(5));
        });
        return transaction;
      });

      if (txs.length > 1) {
        return await sendAllTransactions(
          connection,
          wallet,
          txs,
          [],
          {
            txnPendingMessage: DEXModule.notifications.cancellingOrder.message,
          },
          undefined,
          { ...common.notifications, ...swapNotifications },
        );
      } else {
        return await sendTransaction(connection, wallet, txs[0].instructions, [], true);
      }
    },
    onSuccess: (tx, cancelledOrders) => {
      queryClient.setQueryData<OpenOrdersResponse | undefined>(
        ["openbookOrders", wallet?.publicKey.toBase58()],
        (existing) => {
          if (!existing) {
            return;
          }
          const remainingOrders = existing.orders.filter(
            (order) => !cancelledOrders.some((o) => o.orderId.eq(order.orderId)),
          );
          return { ...existing, orders: remainingOrders };
        },
      );
      // Success notif handled here if not using sendAllTransactions
      if (typeof tx === "string") {
        notify({
          message: DEXModule.notifications.cancelledOrder.message,
          type: "success",
          txId: tx,
        });
      }
    },
    onError: (error: Error) => handlePreSendTxnError(error, "Error cancelling order"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["openbookOrders", wallet?.publicKey.toBase58()] }),
  });
};
